/**
 * One row in the notification popover. Presentational: no data hooks, no queries.
 *
 * Copy comes from the type's i18n keys; a row whose type this build does not recognise
 * falls back to the server-rendered `fallbackTitle` / `fallbackBody`, which is what lets
 * the backend ship a type before the web client knows it.
 */

import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { useRelativeTime } from "@/lib/utils/relative-time";
import { bodyKeyFor, ctaKeyFor, NOTIFICATION_KEYS, resolutionKeyFor } from "@/lib/notifications/i18n-keys";
import { presentationFor } from "@/lib/notifications/presentation";
import type { NotificationTarget } from "@/lib/notifications/target";
import { isUnread, type FeedNotification } from "@/lib/notifications/types";

interface Props {
  notification: FeedNotification;
  /** Fires the row's CTA. Not called for a `{ kind: "none" }` target. */
  onActivate: (target: NotificationTarget) => void;
  /**
   * Whether the reader manages anyone. The deal alerts cover a manager's reports' deals as
   * well as their own, so the body has to say so — see `bodyKeyFor`. Defaults false, which
   * is the narrower claim and therefore the safe one to make without knowing.
   */
  hasDirectReports?: boolean;
  /**
   * The reader's user id. A resolved job-change question reads "You moved Jane…" to the user
   * who acted and "Afeez moved Jane…" to everyone else.
   */
  viewerUserId?: number | string | null;
  /**
   * Declines an open job-change question straight from the row. Accepting always goes through
   * the review dialog — there are several ways to accept and each is confirmed — but saying
   * "not now" needs neither, so it should not cost a dialog.
   */
  onDecline?: () => void;
}

export default function NotificationRow({
  notification,
  onActivate,
  hasDirectReports = false,
  viewerUserId = null,
  onDecline,
}: Props) {
  const { t } = useTranslation();
  const formatRelative = useRelativeTime();

  const { icon: Icon, color } = presentationFor(notification.type);
  const unread = isUnread(notification);
  const hasAction = notification.target.kind !== "none";

  const keys = notification.type ? NOTIFICATION_KEYS[notification.type] : null;
  const title = keys ? t(keys.title, notification.params) : notification.fallbackTitle;
  // A job-change question someone resolved (or the reader dismissed): the backend rewrote the
  // row in place with `params.resolution`, so the body states the outcome instead of asking.
  const resolutionKey = keys ? resolutionKeyFor(notification.params, viewerUserId) : null;
  const body = resolutionKey
    ? t(resolutionKey, {
        ...notification.params,
        actor: notification.params.resolvedByName || t("notifications.resolved.someone"),
      })
    : keys
      ? t(bodyKeyFor(keys, hasDirectReports), notification.params)
      : notification.fallbackBody;

  const ctaKey = ctaKeyFor(notification.target);
  // Only an OPEN question can be declined: once resolved the backend rewrites the target to a
  // plain person, and a dismissed one carries params.resolution.
  const canDecline = Boolean(onDecline) && notification.target.kind === "contactSuggestion" && !resolutionKey;
  const time = formatRelative(notification.createdAt);

  const activate = () => {
    if (hasAction) onActivate(notification.target);
  };

  return (
    <div
      // The old implementation had cursor-pointer and no onClick. The whole row is now
      // the target, with keyboard support — the feed was previously unreachable without
      // a mouse.
      role={hasAction ? "button" : undefined}
      tabIndex={hasAction ? 0 : undefined}
      onClick={activate}
      onKeyDown={e => {
        if (!hasAction) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          activate();
        }
      }}
      className={cn(
        "flex gap-3 px-4 py-3 border-b border-border last:border-0 transition",
        hasAction && "hover:bg-muted/30 cursor-pointer",
        unread && "bg-accent/5",
      )}
    >
      <div
        className={cn(
          "mt-0.5 shrink-0 h-7 w-7 rounded-full flex items-center justify-center bg-muted",
          color,
        )}
      >
        <Icon className="h-3.5 w-3.5" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={cn("text-sm leading-tight", unread ? "font-semibold" : "font-medium")}>
            {title}
          </p>
          {unread && <span className="mt-1.5 h-2 w-2 rounded-full bg-accent shrink-0" />}
        </div>

        {body && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{body}</p>}

        <div className="flex items-center gap-3 mt-1.5">
          {/* The time gives way (truncates) before the actions do: two actions plus "about 12
              hours ago" do not fit a 320px panel, and wrapping each word onto its own line made
              "Not now" / "Review change" read as four buttons. */}
          {time && <span className="text-[11px] text-muted-foreground/60 mr-auto min-w-0 truncate">{time}</span>}
          {canDecline && (
            <button
              type="button"
              className="text-[11px] font-medium text-muted-foreground hover:underline whitespace-nowrap shrink-0"
              onClick={e => {
                // Not the row's action: the row opens the review.
                e.stopPropagation();
                onDecline?.();
              }}
            >
              {t("notifications.cta.notNow")}
            </button>
          )}
          {ctaKey && (
            <button
              type="button"
              className="text-[11px] font-medium text-accent hover:underline whitespace-nowrap shrink-0"
              onClick={e => {
                // The row handles this too; without stopPropagation the CTA fires twice.
                e.stopPropagation();
                activate();
              }}
            >
              {t(ctaKey)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
